import React from 'react';
import { motion } from 'framer-motion';
import { Theme } from '../types';

interface ThemeCardProps {
    theme: Theme;
    isSelected: boolean;
    onToggle: () => void;
    index: number;
}

const ThemeCard: React.FC<ThemeCardProps> = ({
    theme,
    isSelected,
    onToggle,
    index,
}) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
            onClick={onToggle}
        >
            {/* Theme Thumbnail */}
            <img
                src={theme.thumbnail_path}
                alt={theme.name}
                className="w-full h-full object-cover"
            />

            {/* Gradient overlay from bottom - dark purple when selected, dark gray otherwise */}
            <div
                className={`absolute inset-0 bg-gradient-to-t ${
                    isSelected
                        ? 'from-purple-900/90 via-purple-900/40 to-transparent'
                        : 'from-black/70 via-black/20 to-transparent'
                } pointer-events-none transition-all duration-300`}
            />

            {/* Theme Name */}
            <div className="absolute bottom-0 left-0 p-4">
                <h3 className="text-white text-base font-bold uppercase tracking-wider drop-shadow-lg">
                    {theme.name}
                </h3>
            </div>

            {/* Selection Circle */}
            <motion.div
                className="absolute top-3 right-3"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
            >
                <div
                    className={`
                    w-8 h-8 rounded-full border-2 flex items-center justify-center
                    transition-all duration-200
                    ${
                        isSelected
                            ? 'bg-white border-white'
                            : 'bg-white/20 border-white/60 backdrop-blur-sm hover:bg-white/30'
                    }
                `}
                >
                    {isSelected ? (
                        <svg
                            className="w-5 h-5 text-black"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    ) : (
                        <svg
                            className="w-5 h-5 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                            />
                        </svg>
                    )}
                </div>
            </motion.div>

            {/* Hover effect */}
            <motion.div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
        </motion.div>
    );
};

export default ThemeCard;
